import {
  Card, CircularProgress, Container, Divider, TableCell, TableRow,
} from '@material-ui/core';
import { Pagination } from 'api/pagination';
import asbvApi from 'api/query/asbv';
import { ASBV } from 'api/query/asbv/asbv';
import { CatalogueType } from 'api/query/catalogue';
import CatalogueApi from 'api/query/catalogue/catalogue-api';
import { CatalogueExtended } from 'api/query/catalogue/catalogue-extended';
import { SaleType } from 'api/query/catalogue/sale-type.enum';
import { SearchAnimal } from 'api/query/search/search-animal';
import SearchApi from 'api/query/search/search-api';
import { SearchParamsIds } from 'api/query/search/search-params-ids';
import { SearchResponse } from 'api/query/search/search-response';
import { SearchSort } from 'api/query/search/search-sort';
import { Trait } from 'api/query/shared';
import Axios, { CancelTokenSource } from 'axios';
import AnimalCard from 'components/animal-card';
import AnimalIdLink from 'components/animal-id-link';
import AnimalTags from 'components/animal-tags';
import ASBVCell from 'components/asbv-cell';
import ASBVDisclaimer from 'components/asbv-disclaimer';
import { LinkageTag } from 'components/Linkage-tag/linkage-tag';
import LoadContainer from 'components/load-container';
import PercentileTag from 'components/percentile-tag';
import Table from 'components/table';
import { Column } from 'components/table/column';
import LoadMore from 'components/table/load-more';
import TableFade from 'components/table/table-fade';
import deepEqual from 'deep-equal';
import { Catalogues } from 'pages/catalogues/catalogue-constants.enum';
import {
  getAnimalIdsFromCatalogue, getAnimalLot, getAnimalPrice, getCatalogueSortOptions, getTableHeaders, removePriceCol,
} from 'pages/catalogues/catalogue-utils';
import DisplayOptions from 'pages/search/results/display-options';
import { getASBVTooltipContent } from 'pages/search/results/results-utils';
import { ResultsNumConstants } from 'pages/search/results/results-view/results-num-constants.enum';
import { SortDir } from 'pages/search/results/sort-dir.enum';
import ExportButton from 'pages/shared/export-button';
import { useResults } from 'pages/shared/use-results';
import React, {
  memo, useCallback, useEffect, useState,
} from 'react';
import { getASBVs, saveCustomASBVsToStorage } from 'utils/asbv-utils';
import { useBreakpoints } from 'utils/use-breakpoints';
import { CatalogueBanner } from '../../catalogue-banner/catalogue-banner';
import { SalesCatalogueProps } from './sales-catalogue-props';
import './sales-catalogue.scss';

/**
 * Page of individual sale catalogue.
 * @param props
 */
export const SalesCatalogue: React.FC<SalesCatalogueProps> = memo((props) => {
  const {
    db,
    user,
    trait,
  } = props;
  const [catalogue, setCatalogue] = useState<CatalogueExtended>();
  const [data, setData] = useState<SearchResponse>({
    results: [],
    analysisDate: '',
    sortBy: '',
    sortDir: SortDir.BestToWorst,
    totalCount: 0,
  });
  const [columns, setColumns] = useState<Column[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ pageNumber: 1, pageSize: ResultsNumConstants.PageSize });
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [catalogueReady, setCatalogueReady] = useState<boolean>(false);
  const [asbv, setASBV] = useState<ASBV[]>([]);
  const {
    topPercentile,
    bottomPercentile,
    sortByASBV,
    setSortByASBV,
    customASBVs,
    setCustomASBVs,
  } = useResults();
  const [animalsIds, setAnimalsIds] = useState<string>('');
  const { phone } = useBreakpoints();

  /**
   * Calls catalogue api to get more information about selected catalogue.
   */
  const loadCatalogue = useCallback(async (source: CancelTokenSource) => CatalogueApi.getById(props.id, props.db, source.token), [props.db, props.id]);

  /**
   * Calls search api to get animals details that are listed in catalogue.
   */
  const loadResults = useCallback(async (page: Pagination, sortOption: SearchSort, source: CancelTokenSource) => {
    const params: SearchParamsIds = {
      db,
      userType: user,
      subIndex: trait,
      customASBVs,
    };

    const results = await SearchApi.getByIds(props.id, params, page, sortOption, source.token);

    // sets default sort returned by results if state is empty
    if (sortByASBV.sortBy === '' && sortByASBV.sortBy !== results.sortBy) {
      setSortByASBV({ sortBy: results.sortBy, sortDir: results.sortDir });
    }
    // sets new sort returned from column
    if (sortByASBV.sortBy !== sortOption.sortBy) {
      setSortByASBV(sortOption);
    }

    return results;
  }, [db, user, trait, customASBVs, props.id, sortByASBV.sortBy, setSortByASBV]);

  /**
   * Load more results.
   */
  const loadMore = async () => {
    const source = Axios.CancelToken.source();
    setLoadingMore(true);

    const paging = {
      ...pagination,
      pageNumber: pagination.pageNumber + 1,
    };

    const response = await loadResults(paging, sortByASBV, source);

    setTimeout(() => {
      const d = {
        ...response,
        results: data.results.concat(response.results),
      };
      setPagination(paging);
      setLoadingMore(false);
      setData(d);
    }, ResultsNumConstants.Delay);
  };

  /**
   * Sort the result when the header col is selected.
   * @param sortBy
   * @param sortDir
   */
  const sortResult = async (sortBy: string, sortDir: string) => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const sortOption = {
      sortBy,
      sortDir: sortDir === 'desc' ? SortDir.BestToWorst : SortDir.WorstToBest,
    };

    const paging = {
      pageNumber: 1,
      pageSize: ResultsNumConstants.PageSize,
    };

    const d = await loadResults(paging, sortOption, source);
    setTimeout(() => {
      setData(d);
      setLoading(false);
    }, ResultsNumConstants.Delay);
  };

  /**
   * Performs initial setup by getting the customised asbvs from storage (if they exist)
   * and gets the catalogue details.
   *
   * When complete, set the `catalogueReady` flag to true to allow retrieval of animals for this catalogue.
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const setup = async () => {
      const [d, selectedASBVs] = await Promise.all([
        loadCatalogue(source),
        getASBVs(db, trait),
      ]);
      setCatalogue(d);
      setCustomASBVs(selectedASBVs);
      saveCustomASBVsToStorage(selectedASBVs, db, trait);
      setCatalogueReady(true);
    };
    setup();

    return () => {
      source.cancel();
    };
  }, [db, loadCatalogue, setCustomASBVs, trait]);

  /**
   * Gets the first page of catalogue animals.
   *
   * Runs when the component mounts or when the customised asbvs change.
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const sortOption = sortByASBV;

    const paging = {
      pageNumber: 1,
      pageSize: 20,
    };

    if (catalogueReady) {
      const getResults = async () => {
        const d = await loadResults(paging, sortOption, source);
        setData(d);
        setLoading(false);
      };
      getResults();
    }

    return () => {
      source.cancel();
    };
  }, [catalogueReady, loadResults, sortByASBV]);

  /**
   * Sets animals ids to call search api and pass to export button.
   */
  useEffect(() => {
    if (catalogue?.catalogueItems) {
      setAnimalsIds(getAnimalIdsFromCatalogue(catalogue.catalogueItems));
    }
  }, [catalogue]);

  /**
   * Gets ASBVs details for tooltips.
   */
  useEffect(() => {
    const get = async () => {
      const a = await asbvApi.get(db);
      setASBV(a);
    };
    get();
  }, [db]);

  /**
   * Sets the table columns from retrieved data.
   */
  useEffect(() => {
    if (data.results.length) {
      // Default to local sort state, if it hasn't been initialized then use the response sort values
      const cols = getTableHeaders(Catalogues.IndividualSale, sortByASBV.sortBy || data.sortBy, sortByASBV.sortDir || data.sortDir, data.results[0].asbvs);
      // hide price col if not private treaty sale
      if (catalogue?.saleType !== SaleType.PrivateTreatySale) {
        removePriceCol(cols);
      }

      if (!deepEqual(columns, cols)) {
        setColumns(cols);
      }
    }
  }, [catalogue, columns, data.results, data.sortBy, data.sortDir, sortByASBV.sortBy, sortByASBV.sortDir]);

  return (
    <LoadContainer loading={!catalogueReady}>
      {catalogue && (
        <>
          <CatalogueBanner
            catalogue={catalogue}
            catalogueType={Catalogues.IndividualSale}
          />
          {loading && (
            <div className="sg-catalogues__indicator"><CircularProgress color="primary" /></div>
          )}
          {!loading && (
            <Container className={`sg-sale-catalogue ${catalogue.saleType === SaleType.PrivateTreatySale ? 'private-sale' : ''}`}>
              {!phone && (
                <>
                  <div className="sg-sale-catalogue__header">
                    <h4 className="sg-sale-catalogue__title">Animal sale listings</h4>
                    <ExportButton
                      db={props.db}
                      user={props.user}
                      scenario={props.trait}
                      catalogueId={props.id}
                      animalIds={animalsIds}
                      customASBV={customASBVs}
                    />
                  </div>
                  <Divider variant="fullWidth" />
                </>
              )}
              <DisplayOptions
                displayDataOptions={false}
                sortingOptions={data.results.length ? getCatalogueSortOptions(data.results[0]) : []}
              />
              {phone && (
                <>
                  <div className="sg-sale-catalogue__header">
                    <Divider />
                    <h4 className="sg-sale-catalogue__title">Animal sale listings</h4>
                  </div>
                </>
              )}
              {!phone && (
                <Divider variant="fullWidth" />
              )}
              <Container className="sg-sale-catalogue__table">
                <>
                  {!!data.results.length && (
                    <>
                      {phone
                        ? (
                          <>
                            {data.results.map((animal: SearchAnimal, index: number) => (
                              <TableFade
                                in
                                customDelay={(index % 20) * 50}
                                key={animal.animalId}
                              >
                                <AnimalCard
                                  animal={animal}
                                  db={props.db}
                                  trait={props.trait}
                                  lot={getAnimalLot(catalogue.catalogueItems, animal.animalId)}
                                  price={catalogue.saleType === SaleType.PrivateTreatySale ? getAnimalPrice(catalogue.catalogueItems, animal.animalId) : ''}
                                  displayPrice={catalogue.saleType === SaleType.PrivateTreatySale}
                                  isCatalogue
                                  type={CatalogueType.Sales}
                                />
                              </TableFade>
                            ))}
                            <LoadMore
                              loading={loadingMore!}
                              loadedResults={data.results.length!}
                              totalCount={data.totalCount!}
                              onLoadMore={loadMore}
                            />
                          </>
                        )
                        : (
                          <Table
                            columns={columns}
                            isVerticallyScrolled
                            scrollShadow
                            stickyHeader
                            totalCount={data.totalCount}
                            resultsCount={data.results.length}
                            loading={loading}
                            loadingMore={loadingMore}
                            enableSort
                            getHeaderTooltipContent={(id) => getASBVTooltipContent(asbv, id)}
                            onHeaderSelected={(col, dir) => sortResult(col, dir)}
                            onLoadMore={loadMore}
                          >
                            {data.results.map((animal: SearchAnimal, index: number) => (
                              <TableFade
                                in
                                customDelay={(index % 20) * 50}
                                key={animal.animalId}
                              >
                                <Card
                                  component={TableRow}
                                  className="sg-table__row"
                                >
                                  <TableCell className="cell sticky sg-sale-catalogue__lot">{getAnimalLot(catalogue.catalogueItems, animal.animalId)}</TableCell>
                                  {catalogue.saleType === SaleType.PrivateTreatySale
                                    && <TableCell className="cell sticky sg-sale-catalogue__price">{getAnimalPrice(catalogue.catalogueItems, animal.animalId)}</TableCell>}
                                  <TableCell className="cell sticky sg-sale-catalogue__title">
                                    <AnimalIdLink
                                      id={animal.animalId}
                                      label={animal.studId}
                                      database={props.db}
                                      trait={props.trait}
                                    />
                                    <div><AnimalTags animal={animal} /></div>
                                  </TableCell>
                                  {animal.asbvs.map((t: Trait) => (
                                    <TableCell
                                      className="cell cell__data"
                                      key={t.name}
                                      align="center"
                                    >
                                      <>
                                        <ASBVCell asbv={t} />
                                        <div className="data__tag">
                                          <PercentileTag
                                            percentile={t.percentile}
                                            checkVisiblity
                                            hasLeader={t.hasLeader}
                                            isRbv={t.isRbv}
                                            selectedTop={topPercentile}
                                            selectedBottom={bottomPercentile}
                                            isRange
                                          />
                                          <LinkageTag
                                            accuracyType={t.accuracyType}
                                            accuracy={t.accuracy}
                                          />
                                        </div>
                                      </>
                                    </TableCell>
                                  ))}
                                  <TableCell className="cell sticky sg-sale-catalogue__action" />
                                </Card>
                              </TableFade>
                            ))}
                          </Table>
                        )}
                    </>
                  )}
                </>
                <ASBVDisclaimer />
              </Container>
            </Container>
          )}
        </>
      )}
    </LoadContainer>
  );
});

(SalesCatalogue as any).whyDidYouRender = true;
